import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, Tab, Tabs } from '@mui/material';
import AppLayout from '../../layout/app.layout';
import { viewDataById } from '../../services/apps.service';
import ROUTE_PATH from '../../config/routes.config';
import Loading from '../../components/table-elements/loading.component';
import SnackbarInfo from '../../components/common/snackbar-info.component';
import PageNotFoundView from '../errors/not-found.view';
import ViewGridData from '../../components/table-elements/view-grid-data.component';
import {
  VIEW_BASICINFO_COLUMNS,
  VIEW_SUBSCRIPTIONS_COLUMNS,
  VIEW_USER_ON_BOARD_MESSAGE_COLUMNS,
} from '../../config/module-configs/apps.config';
import AssignTags from '../../components/advertisers/assign-tags.component';
import Iconify from '../../components/common/iconify.component';
import replaceIdUrl from '../../utils/url.util';
import useAccess from '../../hooks/access.hook';
import AppThirdPartyIntegrationsView from './app-third-party-integrations.view';
import AppWebsiteIAPPlans from '../../components/content/app-website-iap-plans.component';
import AppWebsiteContent from '../../components/content/app-website-content.component';
import AppWebsiteFields from '../../components/content/app-website-fields.component';
import AppAssetsTheme from '../../components/content/app-assets-theme.component';

const TABS_CONFIG = [
  {
    value: 'basicInfo',
    label: 'App Details',
    columns: VIEW_BASICINFO_COLUMNS,
  },
  {
    value: 'monetizationType',
    label: 'Monetization Type',
    columns: [],
  },
  {
    value: 'subscription',
    label: 'Monetization Settings',
    columns: VIEW_SUBSCRIPTIONS_COLUMNS,
  },
  {
    value: 'websiteIAPPlans',
    label: 'Website IAP Plans',
    component: AppWebsiteIAPPlans,
  },
  {
    value: 'userOnboardMsg',
    label: 'In-App Messaging',
    columns: VIEW_USER_ON_BOARD_MESSAGE_COLUMNS,
  },
  {
    value: 'thirdPartyIntegrations',
    label: 'Third Party Integrations',
    columns: [],
  },
  {
    value: 'websiteContent',
    label: 'Website Content',
    component: AppWebsiteContent,
  },
  {
    value: 'websiteFields',
    label: 'Website Fields',
    component: AppWebsiteFields,
  },
  { value: 'assetsTheme', label: 'Assets & Theme', component: AppAssetsTheme },
];

const AppViewDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasOperationAccess } = useAccess();
  const [tabValue, setTabValue] = useState(TABS_CONFIG[0].value);
  const [loading, setLoading] = useState(true);
  const [showPageNotFound, setShowPageNotFound] = useState(false);
  const [channelDetail, setChannelDetail] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  useEffect(() => {
    viewDataById(id)
      .then((res) => {
        setChannelDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setShowPageNotFound(true);
          return;
        }
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [id]);

  return Number.isNaN(Number(id)) || showPageNotFound ? (
    <PageNotFoundView />
  ) : (
    <AppLayout
      pageTitle={`View Channel Details - ${channelDetail.title}`}
      showBack
      handleBack={() => navigate(ROUTE_PATH.CONTENT_ALL_CHANNELS)}
    >
      <Box display="flex" justifyContent="flex-end" my={1}>
        {hasOperationAccess('channel_settings', 'edit') && (
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="bx:edit" />}
            onClick={() => {
              const url = replaceIdUrl(ROUTE_PATH.CONTENT_CHANNEL_EDIT, id);
              navigate(url);
            }}
            disabled={loading}
          >
            Switch To Edit Mode
          </Button>
        )}
      </Box>
      <Box height="100%" display="flex">
        <Tabs
          value={tabValue}
          aria-label="tabs"
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="transparent"
          sx={{ minWidth: 150, maxWidth: 150 }}
          onChange={(_, v) => setTabValue(v)}
        >
          {TABS_CONFIG.map((tab, index) => (
            <Tab
              key={index + 1}
              value={tab.value}
              label={tab.label}
              sx={{
                background: tabValue === tab.value && '#fff',
                borderLeft: tabValue === tab.value && '2px solid #FD0100',
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            />
          ))}
        </Tabs>

        <Box flexGrow={1}>
          {loading && <Loading />}

          {!loading &&
            (tabValue === 'websiteIAPPlans' ||
            tabValue === 'websiteContent' ||
            tabValue === 'websiteFields' ||
            tabValue === 'assetsTheme' ? (
              TABS_CONFIG.filter((item) => item.value === tabValue)?.map(
                ({ value, component: Component }) =>
                  tabValue === value && (
                    <Component
                      key={value}
                      channelDetail={channelDetail}
                      viewMode
                    />
                  )
              )
            ) : (
              <>
                {tabValue !== 'monetizationType' &&
                  tabValue !== 'thirdPartyIntegrations' && (
                    <ViewGridData
                      columns={
                        TABS_CONFIG.filter((tab) => tab.value === tabValue)[0]
                          ?.columns
                      }
                      row={channelDetail}
                    />
                  )}
                {tabValue === 'monetizationType' && (
                  <Box component={Paper} p={2}>
                    <AssignTags channelId={id} fromChannel viewOnly />
                  </Box>
                )}
                {tabValue === 'thirdPartyIntegrations' && (
                  <Box component={Paper} p={1}>
                    <AppThirdPartyIntegrationsView
                      channelDetail={channelDetail}
                    />
                  </Box>
                )}
              </>
            ))}
        </Box>
      </Box>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </AppLayout>
  );
};

export default AppViewDetails;
